import {useEffect, useState} from 'react'

/** Raw session token or legacy JWT for x-access-token header. */
export const AdminAuth = (): string => {
  const user = localStorage.getItem('admin')
  if (!user) return ''
  try {
    const user_data = JSON.parse(user)
    return user_data.JWToken || user_data.token || ''
  } catch {
    return ''
  }
}

export const getAdminProfile = (): Record<string, unknown> | null => {
  const user = localStorage.getItem('admin')
  if (!user) return null
  try {
    return JSON.parse(user) as Record<string, unknown>
  } catch {
    return null
  }
}

export const clearAdminAuth = (): void => {
  localStorage.removeItem('admin')
}

/** Merge fields into stored admin profile (e.g. after PATCH name on server). */
export function mergeAdminStoredProfile(partial: Record<string, unknown>): void {
  const raw = localStorage.getItem('admin')
  if (!raw) return
  try {
    const cur = JSON.parse(raw) as Record<string, unknown>
    const next = {...cur, ...partial}
    let changed = false
    for (const key of Object.keys(partial)) {
      if (cur[key] !== next[key]) {
        changed = true
        break
      }
    }
    if (!changed) return
    localStorage.setItem('admin', JSON.stringify(next))
    window.dispatchEvent(new Event('shrido-admin-profile-changed'))
  } catch {
    /* ignore */
  }
}

export type AdminMenuProfile = {
  email: string
  displayName: string
  initials: string
  role: string
  roleLabel: string
  /** Public image URL when set (same key as API / localStorage). */
  avatar_url?: string
}

function formatAdminRoleLabel(role: string): string {
  const r = role.trim()
  if (!r) return 'Admin'
  return r
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function initialsFromNameOrEmail(name: string, email: string): string {
  const n = name.trim()
  if (n.length > 0) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      const a = parts[0][0] || ''
      const b = parts[1][0] || ''
      return (a + b).toUpperCase()
    }
    if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase()
    if (parts[0].length === 1) return parts[0].toUpperCase()
  }
  const local = (email.split('@')[0] || 'A').replace(/[^a-zA-Z0-9]/g, '')
  return (local.slice(0, 2) || 'A').toUpperCase()
}

/** Identity block for header / profile menu (from session JSON after login). */
export function getAdminMenuProfile(): AdminMenuProfile | null {
  const p = getAdminProfile()
  if (!p) return null
  const email = typeof p.email === 'string' ? p.email.trim() : ''
  const rawName = typeof p.name === 'string' ? p.name.trim() : ''
  if (!email && !rawName) return null
  const displayName = rawName || email.split('@')[0] || 'Admin'
  const initials = initialsFromNameOrEmail(rawName, email || displayName)
  const role = typeof p.role === 'string' ? p.role.trim() : ''
  const roleLabel = formatAdminRoleLabel(role)
  const av =
    typeof p.avatar_url === 'string' && p.avatar_url.trim() ? p.avatar_url.trim() : undefined
  return {email, displayName, initials, role, roleLabel, avatar_url: av}
}

/** Re-reads menu profile when local session is updated (name, role, login). */
export function useAdminMenuProfile(): AdminMenuProfile | null {
  const [, bump] = useState(0)
  useEffect(() => {
    const onChange = () => bump((n) => n + 1)
    window.addEventListener('shrido-admin-profile-changed', onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener('shrido-admin-profile-changed', onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])
  return getAdminMenuProfile()
}
